import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import { Typography } from "@material-ui/core";
import { PAGING_SIZES } from "../constants/index";

interface PaginationProps {
	//The number of rows in a full page
	rowsPerPage: number;
	//The length of the current page
	currentPageLength: number;
	//The current number of the page
	pageNumber: number;
	//A callback function to set the page number in the parent.
	setPageFunction: (newPageNumber: number) => void;
	//A callback function to set the rows per page in the parent.
	setRowsPerPageFunction: (newRowsPerPage: number) => void;
}

const useStyles = makeStyles((theme) => ({
	root: {
		flexShrink: 0,
		color: theme.palette.text.secondary,
	},
}));

/**
 * Accepts a page number, current page length, and number of rows in the page
 * to be displayed. Also needed are two call back functions. One for setting the rows in the parent,
 * called upon a change in the rows dropdown. A second call back function is used for setting the page number
 * in the parent upon a page change. On row change this function is also called as the page is reset to 0.
 *
 * @param data
 */
export const Pagination = (data: PaginationProps) => {
	function handlePageChange(_event, newPage) {
		data.setPageFunction(newPage);
	}

	function handleRowPerPageChange(event) {
		data.setRowsPerPageFunction(parseInt(event.target.value, 10));
		data.setPageFunction(0);
	}

	const classes = useStyles();

	function handleFirstPageButtonClick(event) {
		handlePageChange(event, 0);
	}

	function handleBackButtonClick(event) {
		handlePageChange(event, data.pageNumber - 1);
	}

	function handleNextButtonClick(event) {
		handlePageChange(event, data.pageNumber + 1);
	}

	const startNumber = data.pageNumber * data.rowsPerPage + 1;

	return (
		<div className={classes.root}>
			<Box display="flex" alignItems="flex-start">
				<Box p={1}>
					<Select
						value={data.rowsPerPage}
						data-testid="row-select"
						onChange={handleRowPerPageChange}
						inputProps={{
							name: "rowsPerpage",
							id: "rowsPerPage-simple",
						}}
					>
						{PAGING_SIZES.map((size) => {
							return (
								<MenuItem key={size} value={size}>
									{size}
								</MenuItem>
							);
						})}
					</Select>
				</Box>
				<Box p={2}>
					<Typography data-testid="row-display" variant="body2">
						{startNumber} - {startNumber + data.currentPageLength - 1}
					</Typography>
				</Box>
				<Box>
					<IconButton
						onClick={handleFirstPageButtonClick}
						disabled={data.pageNumber === 0}
						data-testid="first-page-button"
						aria-label="first page"
					>
						<FirstPageIcon style={{ paddingRight: 0 }} />
					</IconButton>
					<IconButton
						onClick={handleBackButtonClick}
						disabled={data.pageNumber === 0}
						data-testid="prev-page-button"
						aria-label="previous page"
					>
						<KeyboardArrowLeft />
					</IconButton>
					<Typography data-testid="page-display" variant="body2" component="span">
						{data.pageNumber + 1}
					</Typography>
					<IconButton
						onClick={handleNextButtonClick}
						disabled={data.currentPageLength !== data.rowsPerPage}
						aria-label="next page"
						data-testid="next-page-button"
					>
						<KeyboardArrowRight />
					</IconButton>
				</Box>
			</Box>
		</div>
	);
};
